import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '../ui/sheet';
import {
  Sparkles, Rocket, Download, Copy, Loader2, User, Gauge, CalendarClock,
  Network, Radar, Users, GraduationCap, Wand2, ListChecks,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pill = ({ children }) => (
  <span className="inline-block px-2.5 py-1 rounded-full bg-cyan-500/10 border border-cyan-400/25 text-cyan-200 text-xs mr-2 mb-2">{children}</span>
);

const Section = ({ icon: Icon, title, children, id }) => (
  <div id={id} className="rounded-2xl border border-white/10 bg-slate-800/50 backdrop-blur-xl p-5 scroll-mt-24" data-testid={`pack-section-${id}`}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-lg bg-cyan-400/15 text-cyan-300 flex items-center justify-center"><Icon className="w-4 h-4" /></div>
      <h3 className="font-outfit text-lg font-semibold text-white">{title}</h3>
    </div>
    {children}
  </div>
);

const List = ({ items, className = '' }) => (
  <ul className={`space-y-1.5 ${className}`}>
    {(items || []).map((t, i) => (
      <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-cyan-400 mt-1">›</span><span>{t}</span></li>
    ))}
  </ul>
);

export default function LaunchPackViewer({ studentId }) {
  const { getAuthHeader } = useAuth();
  const [status, setStatus] = useState('loading');
  const [pack, setPack] = useState(null);
  const pollRef = useRef(null);
  const qs = studentId ? `?student_id=${studentId}` : '';

  const fetchPack = async () => {
    try {
      const r = await axios.get(`${API}/launch/launch-pack${qs}`, getAuthHeader ? getAuthHeader() : {});
      setStatus(r.data.status || 'none');
      if (r.data.pack) setPack(r.data.pack);
      return r.data.status;
    } catch { setStatus('none'); return 'none'; }
  };

  useEffect(() => {
    fetchPack();
    return () => pollRef.current && clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = () => {
    pollRef.current && clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const st = await fetchPack();
      if (st === 'ready' || st === 'error') {
        clearInterval(pollRef.current);
        if (st === 'error') toast.error('Generation failed, please try again.');
        if (st === 'ready') toast.success('Your Launch Pack is ready!');
      }
    }, 4000);
  };

  const generate = async (regenerate = false) => {
    setStatus('generating');
    try {
      await axios.post(`${API}/launch/launch-pack/generate${qs}${qs ? '&' : '?'}regenerate=${regenerate}`, {}, getAuthHeader ? getAuthHeader() : {});
      startPolling();
    } catch {
      setStatus('none');
      toast.error('Could not start generation.');
    }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copied to clipboard'); };

  // ── Not generated yet ──
  if (status === 'none' || status === 'loading') {
    return (
      <div className="rounded-2xl border border-cyan-400/40 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 p-6" data-testid="launch-pack-cta">
        <div className="flex items-center gap-2 mb-2"><Sparkles className="w-5 h-5 text-cyan-300" /><h2 className="text-lg font-semibold text-white">FuturePath Launch Pack</h2></div>
        <p className="text-sm text-slate-300 max-w-2xl">Generate your personalized action layer with NextMove AI, Success Profile, Launch Readiness Score, a 30/90/12 plan, Skill Gap Map, Opportunity Matches, plus parent &amp; counselor briefs and ready-to-send action drafts.</p>
        <button onClick={() => generate(false)} disabled={status === 'loading'} className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold active:scale-95 transition disabled:opacity-60" data-testid="generate-launch-pack-btn">
          <Rocket className="w-4 h-4" /> Generate Launch Pack
        </button>
      </div>
    );
  }

  // ── Generating ──
  if (status === 'generating' || (!pack && status !== 'ready')) {
    return (
      <div className="rounded-2xl border border-white/10 bg-slate-800/50 p-8 text-center" data-testid="launch-pack-generating">
        <Loader2 className="w-8 h-8 text-cyan-300 animate-spin mx-auto" />
        <p className="text-white font-semibold mt-3">NextMove AI is building your Launch Pack…</p>
        <p className="text-slate-300 text-sm mt-1">This takes about a minute. You can keep exploring, we'll keep working.</p>
      </div>
    );
  }

  if (!pack) return null;

  const ssp = pack.student_success_profile || {};
  const lrs = pack.launch_readiness_score || {};
  const ap = pack.action_plan || {};
  const sgm = pack.skill_gap_map || {};
  const ppr = pack.parent_progress_report || {};
  const cb = pack.counselor_brief || {};

  return (
    <div className="space-y-5" data-testid="launch-pack-viewer">
      {/* Header / actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-cyan-300">FuturePath Launch Pack</p>
          <h2 className="font-outfit text-2xl font-bold text-white">Your personalized game plan</h2>
        </div>
        <div className="flex items-center gap-2">
          <Sheet>
            <SheetTrigger asChild>
              <button className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold active:scale-95 transition" data-testid="open-nextmove-drawer">
                <Wand2 className="w-4 h-4" /> NextMove AI
              </button>
            </SheetTrigger>
            <SheetContent className="bg-slate-900 border-slate-700 overflow-y-auto w-full sm:max-w-lg" data-testid="nextmove-drawer">
              <SheetHeader><SheetTitle className="text-white flex items-center gap-2"><Wand2 className="w-5 h-5 text-cyan-300" /> NextMove AI, ready-to-use drafts</SheetTitle></SheetHeader>
              <SheetDescription className="text-xs text-slate-300 mt-2">Review, edit, and copy. Nothing is sent automatically.</SheetDescription>
              <div className="space-y-4 mt-4">
                {(pack.nextmove_actions || []).map((a, i) => (
                  <div key={i} className="rounded-xl border border-slate-700 bg-slate-800/60 p-4" data-testid={`nextmove-action-${i}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div><span className="text-[10px] uppercase tracking-wide text-cyan-300">{a.type}</span><p className="text-sm font-semibold text-white">{a.title}</p></div>
                      <button onClick={() => copy(a.draft)} className="text-slate-300 hover:text-white p-1.5 rounded-lg hover:bg-slate-700" data-testid={`copy-nextmove-${i}`}><Copy className="w-4 h-4" /></button>
                    </div>
                    <pre className="text-sm text-slate-300 whitespace-pre-wrap font-inter leading-relaxed">{a.draft}</pre>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-600 text-slate-300 text-sm hover:text-white" data-testid="download-pack-btn"><Download className="w-4 h-4" /> Download</button>
          <button onClick={() => generate(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-slate-300 text-sm hover:text-white" data-testid="regenerate-pack-btn">Regenerate</button>
        </div>
      </div>

      <Section icon={User} title="Student Success Profile" id="profile">
        <p className="text-sm text-slate-300 mb-3">{ssp.summary}</p>
        {ssp.personality_type && <p className="text-sm text-slate-300 mb-2"><span className="text-slate-300">Personality:</span> {ssp.personality_type}</p>}
        {ssp.lifestyle_goals && <p className="text-sm text-slate-300 mb-3"><span className="text-slate-300">Lifestyle goals:</span> {ssp.lifestyle_goals}</p>}
        <div>{(ssp.strengths || []).map((x, i) => <Pill key={i}>{x}</Pill>)}</div>
        <div className="mt-1">{(ssp.career_interests || []).map((x, i) => <Pill key={`c${i}`}>{x}</Pill>)}</div>
      </Section>

      <Section icon={Gauge} title="Launch Readiness Score" id="readiness">
        <p className="text-cyan-300 font-semibold mb-3">{lrs.overall_level}</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {(lrs.dimensions || []).map((d, i) => (
            <div key={i} className="rounded-xl border border-slate-700 bg-slate-900/40 p-3">
              <div className="flex items-center justify-between"><span className="text-sm text-white">{d.name}</span><span className="text-xs text-cyan-300">{d.level}</span></div>
              <p className="text-xs text-slate-300 mt-1">{d.note}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={CalendarClock} title="30 / 90 / 12-Month Action Plan" id="plan">
        <div className="grid md:grid-cols-3 gap-4">
          {[['Next 30 days', ap.next_30_days], ['Next 90 days', ap.next_90_days], ['Next 12 months', ap.next_12_months]].map(([t, items]) => (
            <div key={t}><p className="text-xs uppercase tracking-wide text-cyan-300 mb-2">{t}</p><List items={items} /></div>
          ))}
        </div>
      </Section>

      <Section icon={Network} title="Skill Gap Map" id="skills">
        <div className="grid md:grid-cols-2 gap-4">
          <div><p className="text-xs uppercase tracking-wide text-slate-300 mb-2">Current strengths</p><div>{(sgm.current_strengths || []).map((x, i) => <Pill key={i}>{x}</Pill>)}</div></div>
          <div><p className="text-xs uppercase tracking-wide text-slate-300 mb-2">Priority skills</p><div>{(sgm.priority_skills || []).map((x, i) => <Pill key={i}>{x}</Pill>)}</div></div>
        </div>
        <p className="text-xs uppercase tracking-wide text-slate-300 mt-3 mb-2">Suggested actions</p>
        <List items={sgm.suggested_actions} />
      </Section>

      <Section icon={Radar} title="Opportunity Matches" id="opportunities">
        <div className="grid sm:grid-cols-2 gap-2">
          {(pack.opportunity_matches || []).map((o, i) => (
            <div key={i} className="rounded-xl border border-slate-700 bg-slate-900/40 p-3">
              <span className="text-[10px] uppercase tracking-wide text-cyan-300">{o.category}</span>
              <p className="text-sm font-semibold text-white">{o.title}</p>
              <p className="text-xs text-slate-300 mt-1">{o.why}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section icon={Users} title="Parent Progress Report" id="parent">
        {[['Focus', ppr.focus], ['Progress', ppr.progress], ["What's missing", ppr.whats_missing], ['Decisions needing support', ppr.decisions_needing_support], ['Financial considerations', ppr.financial_considerations], ['Next best action', ppr.next_best_action]].map(([k, v]) => v && (
          <p key={k} className="text-sm text-slate-300 mb-2"><span className="text-slate-300">{k}:</span> {v}</p>
        ))}
      </Section>

      <Section icon={GraduationCap} title="Counselor Action Brief" id="counselor">
        {cb.student_goal && <p className="text-sm text-slate-300 mb-2"><span className="text-slate-300">Goal:</span> {cb.student_goal}</p>}
        {cb.recommended_pathway && <p className="text-sm text-slate-300 mb-2"><span className="text-slate-300">Recommended pathway:</span> {cb.recommended_pathway}</p>}
        <p className="text-xs uppercase tracking-wide text-slate-300 mt-2 mb-1">Skill gaps</p><List items={cb.skill_gaps} />
        <p className="text-xs uppercase tracking-wide text-slate-300 mt-3 mb-1">Questions to ask the counselor</p><List items={cb.questions_to_ask} />
        {cb.support_needed && <p className="text-sm text-slate-300 mt-3"><span className="text-slate-300">Support needed:</span> {cb.support_needed}</p>}
      </Section>

      <p className="text-xs text-slate-300 text-center pt-2">This is your living game plan, not a prediction. Regenerate it anytime your profile grows.</p>
    </div>
  );
}
